import React from 'react';
import {
    Card,
    Typography,
    TextField,
    Button,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Tooltip,
    Fade,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ExpandMore, HelpOutline } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: 500,
        maxHeight: '90vh',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
    },
    mainSection: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    inputSection: {
        flexShrink: 0,
    },
    taskListContainer: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: 1,
    },
    taskList: {
        flex: 1,
        minHeight: 0,
        maxHeight: 240,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.02)',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.15)',
            borderRadius: '4px',
            '&:hover': {
                background: 'rgba(0,0,0,0.25)',
            },
        },
    },
    settingsSection: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
    },
    textField: {
        width: '100%',
        marginBottom: theme.spacing(3),
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(0,0,0,0.02)',
            '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.03)',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: '14px 16px',
        },
    },
    button: {
        width: 'calc(50% - 8px)',
        height: 40,
        borderRadius: 4,
        textTransform: 'none',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        padding: '0 1px',
    },
    accordion: {
        borderRadius: 4,
        marginTop: theme.spacing(-2),
        marginBottom: theme.spacing(-1),
        '&:before': {
            display: 'none',
        },
        '& .MuiAccordionSummary-root': {
            minHeight: 48,
            padding: theme.spacing(0),
            '&.Mui-expanded': {
                borderRadius: '4px 4px 0 0',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
            },
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            color: theme.palette.primary.main,
        },
    },
    accordionDetails: {
        backgroundColor: 'rgba(0,0,0,0.03)',
        margin: theme.spacing(0),
        padding: theme.spacing(2),
        borderRadius: '0 0 4px 4px',
    },
    result: {
        textAlign: 'center',
        padding: theme.spacing(2),
        backgroundColor: '#f5f5f5',
        color: theme.palette.text.primary,
        borderRadius: 4,
        transition: 'all 0.2s ease',
    },
    helpIcon: {
        fontSize: 16,
        marginLeft: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        cursor: 'help',
    },
    fieldLabel: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
    helperText: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 2,
    },
    title: {
        fontSize: '1.25rem',
        fontWeight: 500,
    },
    content: {
        overflowY: 'auto',
        flex: 1,
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.02)',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.15)',
            borderRadius: '4px',
            '&:hover': {
                background: 'rgba(0,0,0,0.25)',
            },
        },
    },
    taskItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        backgroundColor: 'rgba(0,0,0,0.02)',
        marginBottom: 4,
        borderRadius: 4,
        '& .MuiButton-root': {
            minWidth: 'auto',
            padding: theme.spacing(0.5, 1.5),
        },
    },
    taskDescription: {
        marginBottom: 2,
    },
    taskSummary: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1, 2),
        position: 'sticky',
        top: 0,
        zIndex: 1,
        marginBottom: theme.spacing(1),
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(1),
        zIndex: 2,
    },
    advancedContent: {
        maxHeight: 260,
        overflowY: 'auto',
        width: '100%',
        padding: '0 1px',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(0,0,0,0.01)',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.15)',
            borderRadius: '4px',
            '&:hover': {
                background: 'rgba(0,0,0,0.25)',
            },
        },
    },
    addTaskButton: {
        width: '100%',
        marginBottom: 16,
        height: 40,
        borderRadius: 4,
        textTransform: 'none',
    },
}));

// Add this type and constants near the top of the file
type DurationType = 'hours' | 'days' | 'weeks' | 'months';

const DURATION_CONVERSIONS = {
    hours: { toDays: 1 / 8, label: 'hours' },
    days: { toDays: 1, label: 'days' },
    weeks: { toDays: 5, label: 'weeks' },
    months: { toDays: 21, label: 'months' },
};

const DEFAULT_DEV_COUNT = 3;
const DEFAULT_BUG_DUTY_FACTOR = 80;
const DEFAULT_BUFFER_FACTOR = 30;

// Add to the existing constants
const DEFAULT_DURATION_TYPE: DurationType = 'days';

// Add this interface for task type
interface Task {
    id: number;
    days: number;
    description: string;
    originalDuration: number;
    durationType: DurationType;
}

const Estimator = () => {
    const classes = useStyles();

    const [devCount, setDevCount] = React.useState(DEFAULT_DEV_COUNT);
    const [estimatedCalendarDays, setEstimatedCalendarDays] = React.useState(0);
    const [bugDutyFactor, setBugDutyFactor] = React.useState(DEFAULT_BUG_DUTY_FACTOR);
    const [bufferFactor, setBufferFactor] = React.useState(DEFAULT_BUFFER_FACTOR);
    const [tasks, setTasks] = React.useState<Task[]>([]);
    const [currentTaskDays, setCurrentTaskDays] = React.useState(0);
    const [currentTaskDescription, setCurrentTaskDescription] = React.useState('');
    const [durationType, setDurationType] = React.useState<DurationType>(DEFAULT_DURATION_TYPE);

    // Calculate total ideal days from all tasks
    const totalIdealDays = React.useMemo(() => {
        return tasks.reduce((sum, task) => sum + task.days, 0);
    }, [tasks]);

    /**
     * Calculates the estimated calendar days for a project based on ideal dev-days,
     * a bug duty adjustment, a buffer for uncertainty, and the number of developers.
     *
     * @param idealDevDays - The total ideal dev-days estimated for the project.
     * @param numberOfDevelopers - The number of developers working on the project.
     * @param bugDutyFactorPercent - The percentage of time developers can dedicate to this project.
     * @param bufferFactorPercent - The percentage of buffer to add to the estimate.
     * @returns The estimated calendar days required to complete the project.
     */
    function calculateCalendarDays(
        idealDevDays: number,
        numberOfDevelopers: number,
        bugDutyFactorPercent: number = 80,
        bufferFactorPercent: number = 30,
    ): number {
        if (numberOfDevelopers <= 0) {
            throw new Error('Number of developers must be greater than zero.');
        }

        const bugDutyFactorDecimal = bugDutyFactorPercent / 100;
        // Convert buffer percentage to multiplier (e.g., 30% becomes 1.3)
        const bufferMultiplier = 1 + bufferFactorPercent / 100;

        // Adjust the ideal dev-days for bug duty and add the uncertainty buffer
        const adjustedDevDays = idealDevDays * (bufferMultiplier / bugDutyFactorDecimal);

        return adjustedDevDays / numberOfDevelopers;
    }

    // Function to add a new task
    const addTask = () => {
        if (currentTaskDays <= 0) return;

        const daysValue = currentTaskDays * DURATION_CONVERSIONS[durationType].toDays;

        setTasks([
            ...tasks,
            {
                id: Date.now(),
                days: daysValue,
                description: currentTaskDescription,
                originalDuration: currentTaskDays,
                durationType: durationType,
            },
        ]);
        setCurrentTaskDays(0);
        setCurrentTaskDescription('');
    };

    // Function to remove a task
    const removeTask = (taskId: number) => {
        setTasks(tasks.filter((task) => task.id !== taskId));
    };

    // Add this handler for task duration changes
    const handleTaskDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // If empty string, set to 0
        if (!value) {
            setCurrentTaskDays(0);
            return;
        }

        // Convert to number and validate
        const numValue = Number(value);
        // Only update if it's a valid number
        if (!isNaN(numValue)) {
            setCurrentTaskDays(numValue);
        }
    };

    // Add this new function to handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        addTask();
    };

    // Add this helper function
    const formatDuration = (task: Task) => {
        return `${task.originalDuration} ${DURATION_CONVERSIONS[task.durationType].label}`;
    };

    const hasResult = estimatedCalendarDays > 0;

    return (
        <Fade in={true} timeout={300}>
            <Card className={classes.root}>
                <div className={classes.header}>
                    <Typography className={classes.title}>Project Estimator</Typography>
                </div>

                <div className={classes.mainSection}>
                    <div className={classes.inputSection}>
                        <form onSubmit={handleSubmit}>
                            <div className={classes.fieldLabel}>
                                <Tooltip
                                    title={`Enter the number of ${DURATION_CONVERSIONS[durationType].label} this task would take one developer working without interruption`}
                                    arrow
                                >
                                    <span>
                                        Task Ideal{' '}
                                        {DURATION_CONVERSIONS[durationType].label
                                            .charAt(0)
                                            .toUpperCase() +
                                            DURATION_CONVERSIONS[durationType].label.slice(1)}
                                        <HelpOutline className={classes.helpIcon} />
                                    </span>
                                </Tooltip>
                            </div>
                            <TextField
                                className={classes.textField}
                                value={currentTaskDays || ''}
                                onChange={handleTaskDurationChange}
                                variant='outlined'
                                InputProps={{
                                    inputProps: {
                                        min: 0,
                                        step: 1,
                                    },
                                }}
                            />

                            <div className={classes.fieldLabel}>
                                <span>Task Title (optional)</span>
                            </div>
                            <TextField
                                className={classes.textField}
                                value={currentTaskDescription}
                                onChange={(e) => setCurrentTaskDescription(e.target.value)}
                                variant='outlined'
                                placeholder='Enter task title'
                            />

                            <Button
                                className={classes.addTaskButton}
                                variant='contained'
                                color='primary'
                                type='submit'
                            >
                                Add Task
                            </Button>
                        </form>
                    </div>

                    {tasks.length > 0 && (
                        <div className={classes.taskListContainer}>
                            <div className={classes.taskSummary}>
                                <Typography variant='subtitle1'>
                                    Tasks ({tasks.length} tasks, Total: {totalIdealDays.toFixed(1)}{' '}
                                    work days)
                                </Typography>
                            </div>
                            <div className={classes.taskList}>
                                {tasks.map((task) => (
                                    <div key={task.id} className={classes.taskItem}>
                                        <div>
                                            <Typography
                                                variant='body2'
                                                className={classes.taskDescription}
                                            >
                                                {task.description || 'Untitled Task'}
                                            </Typography>
                                            <Typography variant='caption' color='textSecondary'>
                                                {formatDuration(task)} ({task.days.toFixed(1)} work
                                                days)
                                            </Typography>
                                        </div>
                                        <Button size='small' onClick={() => removeTask(task.id)}>
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className={classes.settingsSection}>
                        <div className={classes.fieldLabel}>
                            <Tooltip
                                title='Number of developers who will work on this project simultaneously'
                                arrow
                            >
                                <span>
                                    Number of Developers
                                    <HelpOutline className={classes.helpIcon} />
                                </span>
                            </Tooltip>
                        </div>
                        <TextField
                            className={classes.textField}
                            value={devCount}
                            onChange={(e) => setDevCount(Number(e.target.value))}
                            variant='outlined'
                            InputProps={{
                                inputProps: { min: 1 },
                            }}
                        />

                        <Accordion className={classes.accordion} elevation={0}>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls='advanced-settings-content'
                                id='advanced-settings-header'
                            >
                                <Typography>Advanced Settings</Typography>
                            </AccordionSummary>
                            <AccordionDetails className={classes.accordionDetails}>
                                <div className={classes.advancedContent}>
                                    <div className={classes.fieldLabel}>
                                        <Tooltip
                                            title='Choose the duration unit for task estimates'
                                            arrow
                                        >
                                            <span>
                                                Duration Type
                                                <HelpOutline className={classes.helpIcon} />
                                            </span>
                                        </Tooltip>
                                    </div>
                                    <TextField
                                        select
                                        className={classes.textField}
                                        value={durationType}
                                        onChange={(e) =>
                                            setDurationType(e.target.value as DurationType)
                                        }
                                        variant='outlined'
                                        SelectProps={{
                                            native: true,
                                        }}
                                    >
                                        <option value='hours'>Hours</option>
                                        <option value='days'>Days</option>
                                        <option value='weeks'>Weeks</option>
                                        <option value='months'>Months</option>
                                    </TextField>

                                    <div className={classes.fieldLabel}>
                                        <Tooltip
                                            title='The percentage of time developers can dedicate to project work. For example, 80% means 20% of time is reserved for bug fixes and support work.'
                                            arrow
                                        >
                                            <span>
                                                Bug Duty Factor
                                                <HelpOutline className={classes.helpIcon} />
                                            </span>
                                        </Tooltip>
                                    </div>
                                    <TextField
                                        className={classes.textField}
                                        value={bugDutyFactor}
                                        onChange={(e) => setBugDutyFactor(Number(e.target.value))}
                                        variant='outlined'
                                        helperText='Default: 80% (lower means more time on bug fixes and support)'
                                        InputProps={{
                                            inputProps: {
                                                min: 10,
                                                max: 100,
                                                step: 5,
                                            },
                                            endAdornment: <span style={{ marginRight: 8 }}>%</span>,
                                        }}
                                    />

                                    <div className={classes.fieldLabel}>
                                        <Tooltip
                                            title='Additional buffer to account for project uncertainties, meetings, and unexpected delays. For example, 30% adds a 30% buffer to the estimate.'
                                            arrow
                                        >
                                            <span>
                                                Buffer Factor
                                                <HelpOutline className={classes.helpIcon} />
                                            </span>
                                        </Tooltip>
                                    </div>
                                    <TextField
                                        className={classes.textField}
                                        value={bufferFactor}
                                        onChange={(e) => setBufferFactor(Number(e.target.value))}
                                        variant='outlined'
                                        helperText='Default: 30% (higher means more buffer for uncertainty)'
                                        InputProps={{
                                            inputProps: {
                                                min: 0,
                                                max: 100,
                                                step: 5,
                                            },
                                            endAdornment: <span style={{ marginRight: 8 }}>%</span>,
                                        }}
                                    />
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </div>

                <div className={classes.footer}>
                    <div className={classes.buttonContainer}>
                        <Button
                            className={classes.button}
                            variant='outlined'
                            onClick={() => {
                                setTasks([]);
                                setCurrentTaskDays(0);
                                setCurrentTaskDescription('');
                                setDevCount(DEFAULT_DEV_COUNT);
                                setEstimatedCalendarDays(0);
                                setBugDutyFactor(DEFAULT_BUG_DUTY_FACTOR);
                                setBufferFactor(DEFAULT_BUFFER_FACTOR);
                                setDurationType(DEFAULT_DURATION_TYPE);
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            className={classes.button}
                            variant='contained'
                            color='primary'
                            onClick={() => {
                                setEstimatedCalendarDays(
                                    calculateCalendarDays(
                                        totalIdealDays,
                                        devCount,
                                        bugDutyFactor,
                                        bufferFactor,
                                    ),
                                );
                            }}
                        >
                            Calculate
                        </Button>
                    </div>

                    <Fade in={hasResult}>
                        <div className={`${classes.result} ${hasResult ? 'hasResult' : ''}`}>
                            <Typography variant='h5' style={{ fontWeight: 500 }}>
                                Estimated Business Days: {estimatedCalendarDays.toFixed(0)}
                            </Typography>
                        </div>
                    </Fade>
                </div>
            </Card>
        </Fade>
    );
};

export default Estimator;
